import { ComponentStyleProps } from "../../../../lib/styles/props/component-style-props";
import { SearchResultTableContent, SearchResultTableOptions } from "../types";
import {
  ComponentMultiStyleConfig,
  Table,
  TableContainer,
  Tbody,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import { CMSearchResultRow } from "../../row";
import React, { useMemo } from "react";
import { CMSearchResultHeader } from "../../header";
import { PositionProps } from "@chakra-ui/styled-system";
import { hasValue } from "../../../../lib/util/common-util";

export type CMSearchResultTableProps = {
  /**
   * テーブルの内容
   */
  content: SearchResultTableContent;
  options?: SearchResultTableOptions;

  /**
   * この値を指定すると、ヘッダ行がstickyになる。
   * この値は、その際のtopの値となる。
   */
  stickyHeaderTop?: PositionProps["top"];
  /**
   * この値を指定すると、1列目がstickyになる。
   * この値は、その際のleftの値となる。
   * 注意: カテゴリありのヘッダ(2行に亘るヘッダ)を固定列とするのは未サポート。
   *
   * stickyについての参考: https://125naroom.com/web/3816
   */
  stickyFirstColLeft?: PositionProps["left"];
  /**
   * この値を指定すると、2列目がstickyになる。
   * この値は、その際のleftの値となる。
   * 注意: カテゴリありのヘッダ(2行に亘るヘッダ)を固定列とするのは未サポート。
   */
  stickySecondColLeft?: PositionProps["left"];
} & ComponentStyleProps;

/**
 * 検索結果テーブルを表示するコンポーネント
 */
export const CMSearchResultTable = ({
  content,
  options,

  stickyHeaderTop,
  stickyFirstColLeft,
  stickySecondColLeft,

  sx,
  ...rest
}: CMSearchResultTableProps) => {
  const styles = useMultiStyleConfig("CMSearchResultTableStyles");

  const containerStyle = useMemo(() => {
    return {
      ...styles.container,
      ...sx,
      ...(hasValue(stickyFirstColLeft)
        ? styles.containerStickyFirstCol
        : undefined),
    };
  }, [stickyFirstColLeft, styles, sx]);

  return (
    <TableContainer
      // 参考: https://github.com/chakra-ui/chakra-ui/discussions/5656
      overflowX="unset"
      overflowY="unset"
      {...rest}
      sx={containerStyle}
    >
      <Table sx={styles.table}>
        <CMSearchResultHeader
          content={content.header}
          stickyHeaderTop={stickyHeaderTop}
          stickyFirstColLeft={stickyFirstColLeft}
          stickySecondColLeft={stickySecondColLeft}
          sx={styles.header}
        />
        <Tbody>
          {content.rows.map((c, idx) => (
            <CMSearchResultRow
              key={idx}
              content={c}
              options={options}
              cellStyles={
                idx % 2 === 0 ? styles.evenRowCell : styles.oddRowCell
              }
              stickyFirstColLeft={stickyFirstColLeft}
              stickySecondColLeft={stickySecondColLeft}
              sx={styles.row}
            />
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export const CMSearchResultTableStyles: ComponentMultiStyleConfig = {
  parts: [
    "container",
    "containerStickyFirstCol",
    "table",
    "header",
    "row",
    "evenRowCell",
    "oddRowCell",
  ],
  baseStyle: {
    container: {
      borderWidth: "1px",
      borderColor: "gray.400",
      minW: "max-content",
    },
    containerStickyFirstCol: {
      borderLeft: "none",
    },
    table: {
      // tableLayout: "fixed",
      whiteSpace: "pre",
      borderCollapse: "separate",
      borderSpacing: 0,
      // minW: "max-content",
    },
    header: {},
    row: {
      // _even: {
      //   bgColor: "blue.50",
      // },
    },
    evenRowCell: {
      bgColor: "blue.50",
    },
    oddRowCell: {
      bgColor: "white",
    },
  },
};
